import { useCallback, useEffect, useRef, useState } from "react";
import { useRoomContext } from "@livekit/components-react";
import { RoomEvent } from "livekit-client";
import type { CollabMessage, WhiteboardPoint, WhiteboardStroke } from "./types";

const COLORS = ["#111827", "#dc2626", "#16a34a", "#0f766e", "#ea580c"];

export default function Whiteboard({ onClose }: { onClose: () => void }) {
  const room = useRoomContext();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<WhiteboardStroke[]>([]);
  const drawingRef = useRef<WhiteboardStroke | null>(null);
  const [color, setColor] = useState(COLORS[0]);

  const drawStroke = useCallback((ctx: CanvasRenderingContext2D, stroke: WhiteboardStroke) => {
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.width;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    stroke.points.forEach((p, i) => {
      const x = p.x * ctx.canvas.width;
      const y = p.y * ctx.canvas.height;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
  }, []);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    strokesRef.current.forEach((s) => drawStroke(ctx, s));
  }, [drawStroke]);

  // Canvas'ı kapsayıcısına göre boyutlandır + çizimleri yeniden çiz.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;
    const resize = () => {
      canvas.width = parent.clientWidth;
      canvas.height = parent.clientHeight;
      redraw();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(parent);
    resize();
    return () => ro.disconnect();
  }, [redraw]);

  // Diğer katılımcılardan gelen çizimleri dinle.
  useEffect(() => {
    const handler = (payload: Uint8Array) => {
      try {
        const msg = JSON.parse(new TextDecoder().decode(payload)) as CollabMessage;
        if (msg.kind === "whiteboard-stroke" && msg.stroke) {
          strokesRef.current = [...strokesRef.current, msg.stroke];
          redraw();
        } else if (msg.kind === "whiteboard-clear") {
          strokesRef.current = [];
          redraw();
        }
      } catch {
        // bozuk paketi yok say
      }
    };
    room.on(RoomEvent.DataReceived, handler);
    return () => {
      room.off(RoomEvent.DataReceived, handler);
    };
  }, [room, redraw]);

  const sendData = (msg: CollabMessage) => {
    room.localParticipant.publishData(
      new TextEncoder().encode(JSON.stringify(msg)),
      { reliable: true },
    );
  };

  const getPoint = (e: React.PointerEvent): WhiteboardPoint | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    const p = getPoint(e);
    const canvas = canvasRef.current;
    if (!p || !canvas) return;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = { color, width: 3, points: [p] };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const drawing = drawingRef.current;
    const canvas = canvasRef.current;
    if (!drawing || !canvas) return;
    const p = getPoint(e);
    if (!p) return;
    drawing.points.push(p);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    redraw();
    drawStroke(ctx, drawing);
  };

  const onPointerUp = () => {
    const drawing = drawingRef.current;
    drawingRef.current = null;
    if (!drawing || drawing.points.length === 0) return;
    strokesRef.current = [...strokesRef.current, drawing];
    redraw();
    sendData({ kind: "whiteboard-stroke", stroke: drawing });
  };

  const clearBoard = () => {
    strokesRef.current = [];
    redraw();
    sendData({ kind: "whiteboard-clear" });
  };

  return (
    <div className="w-full sm:w-[420px] shrink-0 border-l border-background-700/50 bg-background-900 flex flex-col">
      <div className="h-12 px-4 flex items-center justify-between border-b border-background-700/50 shrink-0">
        <h3 className="text-sm font-semibold text-background-100">Beyaz Tahta</h3>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={clearBoard}
            className="h-8 px-2.5 rounded-md text-xs font-medium text-background-300 hover:bg-background-700 transition-colors whitespace-nowrap"
          >
            Temizle
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md text-background-300 hover:bg-background-700 transition-colors"
            aria-label="Beyaz tahtayı kapat"
          >
            <i className="ri-close-line" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <div className="px-4 py-2 flex items-center gap-2 border-b border-background-700/50 shrink-0">
        {COLORS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setColor(c)}
            className={`w-6 h-6 rounded-full transition-transform ${
              color === c ? "ring-2 ring-offset-2 ring-offset-background-900 ring-white scale-110" : ""
            }`}
            style={{ backgroundColor: c }}
            aria-label={`Renk ${c}`}
          />
        ))}
      </div>

      <div className="flex-1 relative bg-background-50">
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="w-full h-full touch-none cursor-crosshair"
        />
        {strokesRef.current.length === 0 && (
          <p className="absolute inset-0 flex items-center justify-center text-xs text-foreground-400 pointer-events-none">
            Buraya çiz, tüm sınıf anında görsün.
          </p>
        )}
      </div>
    </div>
  );
}
